import { PdfLayout } from '../PdfLayout';
import { LayoutDashboard, GripHorizontal, MonitorSmartphone, Share2, Palette, Users } from 'lucide-react';

const benefits = [
  {
    icon: LayoutDashboard,
    title: '40+ Widget Types',
    text: 'Values, gauges, multi-tag trends, alarm lists, tables, camera images, buttons, sliders and forms — plus OEE, andon and other production widgets.',
  },
  {
    icon: GripHorizontal,
    title: 'Drag-and-Drop Builder',
    text: 'Drag widgets from the palette onto a grid that scales to the screen, with undo and redo, and unsaved work kept as a draft. No code, no configuration files.',
  },
  {
    icon: MonitorSmartphone,
    title: 'Desktop, Tablet and Phone',
    text: 'Live values stream to every screen as they change. Arrange tablet and phone layouts yourself, or let them reflow from the desktop one.',
  },
  {
    icon: Share2,
    title: 'Kiosk & TV Displays',
    text: 'Boot a wall screen straight into a dashboard, or cycle through dashboards on the full-screen TV display. Public dashboards need no sign-in.',
  },
  {
    icon: Palette,
    title: 'Your Look',
    text: 'Per-widget colors, borders and fonts, reusable style classes, and a dashboard background color or image.',
  },
  {
    icon: Users,
    title: 'Share With the Right People',
    text: 'Choose which user groups can view or edit each dashboard, enforced by the appliance, and duplicate one to start the next.',
  },
];

export function Dashboards() {
  return (
    <PdfLayout
      category="Visualization"
      headline="Build Dashboards in"
      headlineHighlight="Minutes, Not Months"
      description="Click, drag, and done. Choose from 40+ widget types, bind them to live tags, and watch your factory come to life on screen — on a desktop, a tablet, or the TV over the line. No developer required."
      accentColor="#3b82f6"
      benefits={benefits}
    >
      <DashboardMockup />
    </PdfLayout>
  );
}

function DashboardMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Facility Dashboard</span>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {/* Gauge */}
        <div className="glass-card p-2 flex flex-col items-center">
          <div className="w-10 h-10 rounded-full border-[3px] border-brand-primary/20 relative mb-1">
            <div
              className="absolute inset-0.5 rounded-full"
              style={{
                background: 'conic-gradient(#3b82f6 0% 72%, transparent 72% 100%)',
                mask: 'radial-gradient(farthest-side, transparent 55%, #000 56%)',
                WebkitMask: 'radial-gradient(farthest-side, transparent 55%, #000 56%)',
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-text-primary">72%</span>
          </div>
          <span className="text-[7px] text-text-muted">OEE</span>
        </div>

        {/* Value */}
        <div className="glass-card p-2 flex flex-col items-center justify-center">
          <span className="text-sm font-bold text-brand-primary tabular-nums">1,247</span>
          <span className="text-[7px] text-text-muted">Parts Today</span>
        </div>

        {/* Status */}
        <div className="glass-card p-2 flex flex-col items-center justify-center">
          <span className="text-sm font-bold text-status-running tabular-nums">8/10</span>
          <span className="text-[7px] text-text-muted">Machines Up</span>
        </div>

        {/* Chart */}
        <div className="glass-card p-2 col-span-2">
          <span className="text-[7px] text-text-muted block mb-1">Throughput</span>
          <svg viewBox="0 0 200 40" className="w-full h-7">
            <polyline
              points="0,35 25,30 50,22 75,26 100,16 125,18 150,10 175,13 200,6"
              fill="none"
              stroke="#3b82f6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="0,35 25,30 50,22 75,26 100,16 125,18 150,10 175,13 200,6 200,40 0,40"
              fill="url(#pdfChartGrad)"
              strokeWidth="0"
            />
            <defs>
              <linearGradient id="pdfChartGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Status dot */}
        <div className="glass-card p-2 flex flex-col items-center justify-center">
          <div className="w-2.5 h-2.5 rounded-full bg-status-running mb-0.5" />
          <span className="text-[7px] text-text-muted">All Clear</span>
        </div>

        {/* Machine list */}
        <div className="glass-card p-2 col-span-3 space-y-1">
          <span className="text-[7px] text-text-muted block">Machine Status</span>
          {[
            { name: 'CNC-01 Haas VF2', status: 'running', value: '4,200 RPM' },
            { name: 'CNC-02 Mazak QT', status: 'running', value: '3,800 RPM' },
            { name: 'PLC-03 Siemens', status: 'idle', value: 'Waiting' },
          ].map((m) => (
            <div key={m.name} className="flex items-center gap-1.5 text-[7px]">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.status === 'running' ? 'bg-status-running' : 'bg-status-idle'}`} />
              <span className="text-text-secondary truncate flex-1">{m.name}</span>
              <span className="text-text-muted tabular-nums">{m.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
